import React, { useState, useEffect, useCallback } from 'react';
import { eventAuditAPI, usersAPI } from '../services/api';
import './EventAuditPage.css';

const ACTION_LABELS = {
  created: 'Создание',
  updated: 'Изменение',
  deleted: 'Удаление',
  restored: 'Восстановление'
};

const EventAuditPage = ({ onBack }) => {
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterEventId, setFilterEventId] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [restoringId, setRestoringId] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { limit, offset };
      if (filterEventId.trim()) params.eventId = parseInt(filterEventId.trim(), 10);
      if (filterUserId) params.userId = parseInt(filterUserId, 10);
      if (filterAction) params.action = filterAction;
      const data = await eventAuditAPI.getList(params);
      setList(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка загрузки журнала');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [filterEventId, filterUserId, filterAction, offset]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const handleRestore = async (eventId) => {
    if (!window.confirm('Восстановить это мероприятие?')) return;
    try {
      setRestoringId(eventId);
      await eventAuditAPI.restore(eventId);
      await loadAudit();
    } catch (err) {
      const data = err.response?.data;
      const isAlreadyRestored = data?.code === 'EVENT_ALREADY_RESTORED' || (err.response?.status === 400 && data?.error && String(data.error).toLowerCase().includes('уже восстановлено'));
      if (isAlreadyRestored) {
        alert('Это мероприятие уже восстановлено и отображается в календаре.');
      } else {
        alert(data?.error || 'Ошибка восстановления');
      }
    } finally {
      setRestoringId(null);
    }
  };

  const eventDisplay = (row) => {
    const name = row.event_name || (() => {
      const sn = typeof row.snapshot === 'string' ? (() => { try { return JSON.parse(row.snapshot); } catch { return null; } })() : row.snapshot;
      return sn && sn.event && sn.event.name ? sn.event.name : null;
    })();
    const org = row.organization_name || (() => {
      const sn = typeof row.snapshot === 'string' ? (() => { try { return JSON.parse(row.snapshot); } catch { return null; } })() : row.snapshot;
      return sn && sn.event && sn.event.organization_name ? sn.event.organization_name : null;
    })();
    if (name && org) return `${name} (${org})`;
    if (name) return name;
    return `ID: ${row.event_id}`;
  };

  const formatAt = (atStr) => {
    if (!atStr) return '—';
    const d = new Date(atStr);
    return d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div className="event-audit-page">
      <div className="event-audit-header">
        <h1>Журнал мероприятий</h1>
        <div className="event-audit-header-actions">
          <button type="button" className="event-audit-back-btn" onClick={onBack}>
            ← Назад к календарю
          </button>
          <button type="button" className="event-audit-close-btn" onClick={onBack}>
            Закрыть
          </button>
        </div>
      </div>

      <div className="event-audit-filters">
        <label>
          ID мероприятия:
          <input
            type="number"
            min="1"
            value={filterEventId}
            onChange={(e) => { setFilterEventId(e.target.value); setOffset(0); }}
            placeholder="все"
            className="event-audit-input"
          />
        </label>
        <label>
          Пользователь:
          <select
            value={filterUserId}
            onChange={(e) => { setFilterUserId(e.target.value); setOffset(0); }}
            className="event-audit-select"
          >
            <option value="">все</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
            ))}
          </select>
        </label>
        <label>
          Действие:
          <select
            value={filterAction}
            onChange={(e) => { setFilterAction(e.target.value); setOffset(0); }}
            className="event-audit-select"
          >
            <option value="">все</option>
            <option value="created">Создание</option>
            <option value="updated">Изменение</option>
            <option value="deleted">Удаление</option>
            <option value="restored">Восстановление</option>
          </select>
        </label>
        <button type="button" className="event-audit-refresh-btn" onClick={() => loadAudit()}>
          Обновить
        </button>
      </div>

      {error && <div className="event-audit-error">{error}</div>}
      {loading && <div className="event-audit-loading">Загрузка…</div>}

      {!loading && !error && (
        <>
          <div className="event-audit-table-wrap">
            <table className="event-audit-table">
              <thead>
                <tr>
                  <th>Дата и время</th>
                  <th>Пользователь</th>
                  <th>Мероприятие</th>
                  <th>Действие</th>
                  <th>Что изменено</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="event-audit-empty">Записей нет</td>
                  </tr>
                ) : (
                  list.map((row) => (
                    <tr key={row.id}>
                      <td>{formatAt(row.at)}</td>
                      <td>{row.username || (row.user_id ? `ID: ${row.user_id}` : '—')}</td>
                      <td title={row.snapshot ? JSON.stringify(row.snapshot.event) : ''}>
                        {eventDisplay(row)}
                      </td>
                      <td>{ACTION_LABELS[row.action] || row.action}</td>
                      <td className="event-audit-changes">
                        {row.action === 'updated' && row.changes_summary
                          ? row.changes_summary.split('; ').map((part, i) => (
                              <div key={i} className="audit-change-item">• {part}</div>
                            ))
                          : '—'}
                      </td>
                      <td>
                        {row.action === 'deleted' && (
                          <button
                            type="button"
                            className="event-audit-restore-btn"
                            onClick={() => handleRestore(row.event_id)}
                            disabled={restoringId === row.event_id}
                          >
                            {restoringId === row.event_id ? '…' : 'Восстановить'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="event-audit-pagination">
            <button
              type="button"
              className="event-audit-page-btn"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
            >
              Назад
            </button>
            <span className="event-audit-page-info">Строк: {list.length}</span>
            <button
              type="button"
              className="event-audit-page-btn"
              disabled={list.length < limit}
              onClick={() => setOffset((o) => o + limit)}
            >
              Вперёд
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EventAuditPage;
